import type { CustomField, CustomSection } from '@gcv/shared';

import { isScoredQuestion, REQUIRED_WEIGHT_TOTAL } from 'util/risk-assessment.util';

export type SectionWeightState = 'balanced' | 'under' | 'over' | 'unscored';

/** The API rejects weights outside this range. */
export const MIN_WEIGHT = 0;
export const MAX_WEIGHT = 100;

export interface SectionWeightSummary {
  scoredFieldIds: string[];
  addends: number[];
  total: number;
  state: SectionWeightState;
  difference: number;
}

export interface WeightEntry {
  isValid: boolean;
  /** undefined clears the weight. */
  weight?: number;
}

/** Validated rather than coerced, so 150 isn't clamped to 100 and 2.6 isn't rounded. Blank clears. */
export const parseWeightEntry = (raw: string): WeightEntry => {
  const trimmed = raw.trim();

  if (trimmed === '') {
    return { isValid: true, weight: undefined };
  }

  const parsed = Number(trimmed);

  if (!Number.isInteger(parsed) || parsed < MIN_WEIGHT || parsed > MAX_WEIGHT) {
    return { isValid: false };
  }

  return { isValid: true, weight: parsed };
};

export const isWeightedQuestion = (field: CustomField): boolean => isScoredQuestion(field);

/** A section's question-weight total broken into addends. Same scored rule as the publish gate. */
export const getSectionWeightSummary = (section: CustomSection): SectionWeightSummary => {
  const scored = (section?.fields ?? []).filter(isWeightedQuestion);
  const addends = scored.map((field) => (field as { weight?: number }).weight ?? 0);
  const total = addends.reduce((sum, weight) => sum + weight, 0);

  const sectionWeight = (section as { weight?: number })?.weight ?? 0;

  if (scored.length === 0 && sectionWeight > 0) {
    return { scoredFieldIds: [], addends: [], total: 0, state: 'unscored', difference: sectionWeight };
  }

  let state: SectionWeightState = 'balanced';
  if (total < REQUIRED_WEIGHT_TOTAL) {
    state = 'under';
  } else if (total > REQUIRED_WEIGHT_TOTAL) {
    state = 'over';
  }

  return {
    scoredFieldIds: scored.map((field) => field.id),
    addends,
    total,
    state,
    difference: Math.abs(REQUIRED_WEIGHT_TOTAL - total)
  };
};
